(() => {
  if (window.noyocoCartStateVersion === 2) return;
  window.noyocoCartStateVersion = 2;
  const diagnostic = (status, count) => {
    if (!document.documentElement) return;
    document.documentElement.dataset.noyocoCartCrmStatus = status;
    if (Number.isInteger(count)) document.documentElement.dataset.noyocoCartCrmCount = String(count);
  };
  diagnostic('loaded');
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const clean = value => String(value || '').replace(/\s+/g, ' ').trim();
  const decoded = value => new DOMParser().parseFromString(String(value || ''), 'text/html').body.textContent || '';
  const fields = ['variant_id', 'product_id', 'product_title', 'product_type', 'variant_title', 'quantity', 'price_display', 'line_price_display', 'url', 'image', 'composition', 'fabric_origin', 'making'];
  const lean = item => Object.fromEntries(fields.filter(key => item[key] !== undefined).map(key => [key, item[key]]));
  const normalize = (item, labels) => {
    const value = { ...item };
    const doc = new DOMParser().parseFromString(item.proof_html || '', 'text/html');
    for (const row of doc.querySelectorAll('.noyoco-cart-proof__row')) {
      const label = clean(row.querySelector('dt')?.textContent);
      const text = clean(row.querySelector('dd')?.textContent);
      for (const [key, expected] of Object.entries(labels || {})) {
        if (label === clean(expected) && text) value[key] = text;
      }
    }
    value.price_display = decoded(item.price_display);
    value.line_price_display = decoded(item.line_price_display);
    return lean(value);
  };
  const validLines = items => items.filter(item => /^\d+$/.test(String(item.variant_id)) && Number.isInteger(item.quantity) && item.quantity > 0);
  const state = (items, currency, schema, route = root) => {
    const lines = validLines(items);
    return {
      cart_items: lines,
      cart_url: lines.length ? `${location.origin}${route}cart/${lines.map(item => `${item.variant_id}:${item.quantity}`).join(',')}?storefront=true` : '',
      cart_currency: currency || 'EUR',
      cart_schema_version: schema,
      cart_item_count: lines.reduce((sum, item) => sum + item.quantity, 0)
    };
  };
  const readJSON = async path => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await nativeFetch(path, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal
      });
      if (!response.ok) throw new Error('cart-facts');
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  };
  const fromCart = (cart, route = root) => {
    const money = new Intl.NumberFormat(route === '/en/' ? 'en' : 'fr', {
      style: 'currency', currency: cart?.currency || 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2
    });
    return state((cart?.items || []).map(line => lean({
      ...line,
      url: new URL(line.url, location.origin).href,
      price_display: Number.isFinite(line.final_price) ? money.format(line.final_price / 100) : decoded(line.price_display),
      line_price_display: Number.isFinite(line.final_line_price) ? money.format(line.final_line_price / 100) : decoded(line.line_price_display)
    })), cart?.currency, 0, route);
  };
  const readAt = async route => {
    try {
      const saved = await readJSON(`${route}cart?view=noyoco-crm&crm_version=2`);
      if (saved.schema_version !== 1 || !Array.isArray(saved.items)) throw new Error('cart-schema');
      return state(saved.items.map(item => normalize(item, saved.labels)), saved.currency, 2, route);
    } catch (_) {
      const cart = await readJSON(`${route}cart.js`);
      if (!Array.isArray(cart.items)) throw new Error('cart-schema');
      return fromCart(cart, route);
    }
  };
  const identity = value => value.cart_items.map(item => `${item.variant_id}:${item.quantity}`).sort().join(',');
  const read = async () => {
    const primaryRoute = root === '/en/' ? '/en/' : '/';
    const otherRoute = primaryRoute === '/' ? '/en/' : '/';
    const [primaryResult, otherResult] = await Promise.allSettled([readAt(primaryRoute), readAt(otherRoute)]);
    if (primaryResult.status !== 'fulfilled') throw new Error('cart-read');
    const primary = primaryResult.value;
    const other = otherResult.status === 'fulfilled' && identity(otherResult.value) === identity(primary) ? otherResult.value : null;
    const limited = state(primary.cart_items.map(item => {
      const value = { ...item };
      for (const key of ['composition', 'fabric_origin', 'making', 'product_type']) delete value[key];
      return value;
    }), primary.cart_currency, 0, otherRoute);
    return {
      ...primary,
      fr: primaryRoute === '/' ? { ...primary } : (other || limited),
      en: primaryRoute === '/en/' ? { ...primary } : (other || limited)
    };
  };
  let chain = Promise.resolve();
  let lastFingerprint = '';
  let timer;
  const publish = value => {
    const fingerprint = JSON.stringify(value);
    if (fingerprint === lastFingerprint) return;
    lastFingerprint = fingerprint;
    window._learnq = window._learnq || [];
    window._learnq.push(['identify', {
      noyoco_cart_state: value,
      noyoco_cart_item_count: value.cart_item_count,
      noyoco_cart_updated_at: new Date().toISOString()
    }, () => diagnostic('saved', value.cart_item_count)]);
    diagnostic('queued', value.cart_item_count);
  };
  const enqueue = task => {
    chain = chain.then(task, task).catch(() => diagnostic('read-failed'));
    return chain;
  };
  const refresh = () => enqueue(async () => publish(await read()));
  const queueRefresh = () => {
    clearTimeout(timer);
    timer = setTimeout(refresh, 120);
  };
  window.noyocoTrackCart = (item, cart) => {
    clearTimeout(timer);
    return enqueue(async () => {
    let current;
    try {
      current = await read();
      publish(current);
    } catch (_) {
      current = fromCart(cart || { items: [item], currency: 'EUR' });
      publish(current);
    }
    if (current.cart_item_count) {
      window._learnq = window._learnq || [];
      window._learnq.push(['track', 'Added to Cart', { ...item, ...current }]);
    }
    });
  };
  const isCartMutation = input => {
    try {
      const url = new URL(typeof input === 'string' ? input : input.url, location.href);
      return url.origin === location.origin && /\/cart\/(?:add|change|update|clear)(?:\.js|\.json)?\/?$/.test(url.pathname);
    } catch (_) {
      return false;
    }
  };
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    if (response.ok && isCartMutation(args[0])) {
      if (document.documentElement) document.documentElement.dataset.noyocoCartCrmMutation = 'fetch';
      queueRefresh();
    }
    return response;
  };
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    if (isCartMutation(url)) this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) queueRefresh();
    }, { once: true });
    return nativeOpen.call(this, method, url, ...args);
  };
  window.addEventListener('pageshow', queueRefresh);
  window.addEventListener('klaviyoForms', event => {
    if (event.detail?.type === 'submit') {
      lastFingerprint = '';
      setTimeout(queueRefresh, 500);
      setTimeout(() => { lastFingerprint = ''; queueRefresh(); }, 2000);
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', queueRefresh, { once: true });
  else queueRefresh();
})();
